import React, { useState } from 'react'
import { Button } from "@/components/ui/button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog"
import { X, Plus, Filter } from 'lucide-react'

export interface FilterRule {
  id: string
  field: string
  operator: string
  value: string
  connector?: 'AND' | 'OR'
}

export interface FieldConfig {
  key: string
  label: string
  type: 'text' | 'select' | 'number' | 'boolean' | 'date'
  options?: { value: string; label: string }[]
}

interface AdvancedFilterProps {
  fields: FieldConfig[]
  filters: FilterRule[]
  onFiltersChange: (filters: FilterRule[]) => void
  onApply: () => void
  onClear: () => void
}

const OPERATORS = {
  text: [
    { value: 'contains', label: 'Contains' },
    { value: 'equals', label: 'Equals' },
    { value: 'in', label: 'Is one of (comma-separated)' },
    { value: 'starts_with', label: 'Starts with' },
    { value: 'ends_with', label: 'Ends with' },
    { value: 'not_contains', label: 'Does not contain' },
    { value: 'is_empty', label: 'Is empty' },
    { value: 'is_not_empty', label: 'Is not empty' }
  ],
  select: [
    { value: 'equals', label: 'Equals' },
    { value: 'not_equals', label: 'Not equals' }
  ],
  number: [
    { value: 'equals', label: 'Equals' },
    { value: 'greater_than', label: 'Greater than' },
    { value: 'less_than', label: 'Less than' },
    { value: 'greater_equal', label: 'Greater or equal' },
    { value: 'less_equal', label: 'Less or equal' }
  ],
  boolean: [
    { value: 'equals', label: 'Is' }
  ],
  date: [
    { value: 'equals', label: 'Equals' },
    { value: 'greater_than', label: 'After' },
    { value: 'less_than', label: 'Before' },
    { value: 'greater_equal', label: 'On or after' },
    { value: 'less_equal', label: 'On or before' },
    { value: 'is_empty', label: 'Is empty' },
    { value: 'is_not_empty', label: 'Is not empty' }
  ]
}

export function AdvancedFilter({ 
  fields, 
  filters, 
  onFiltersChange, 
  onApply, 
  onClear 
}: AdvancedFilterProps) {
  const [open, setOpen] = useState(false)
  const [localFilters, setLocalFilters] = useState<FilterRule[]>(filters)

  React.useEffect(() => {
    setLocalFilters(filters)
  }, [filters])

  const addFilter = () => {
    const newFilter: FilterRule = {
      id: Date.now().toString(),
      field: fields[0]?.key || '',
      operator: 'contains',
      value: '',
      connector: localFilters.length > 0 ? 'AND' : undefined
    }
    setLocalFilters([...localFilters, newFilter])
  }

  const updateFilter = (id: string, updates: Partial<FilterRule>) => {
    setLocalFilters(localFilters.map(filter => 
      filter.id === id ? { ...filter, ...updates } : filter
    ))
  }

  const removeFilter = (id: string) => {
    const newFilters = localFilters.filter(filter => filter.id !== id)
    // Remove connector from first filter if it exists
    if (newFilters.length > 0 && newFilters[0].connector) {
      newFilters[0] = { ...newFilters[0], connector: undefined }
    }
    setLocalFilters(newFilters)
  }

  const getFieldConfig = (fieldKey: string) => {
    return fields.find(field => field.key === fieldKey)
  }

  const getOperatorsForField = (fieldKey: string) => {
    const field = getFieldConfig(fieldKey)
    return field ? OPERATORS[field.type] : OPERATORS.text
  }

  const needsValueInput = (operator: string) => {
    return !['is_empty', 'is_not_empty'].includes(operator)
  }

  const activeFiltersCount = filters.filter(f => f.value || !needsValueInput(f.operator)).length

  const handleApply = () => {
    const validFilters = localFilters.filter(f => f.field && f.operator)
    onFiltersChange(validFilters)
    onApply()
    setOpen(false)
  }

  const handleClear = () => {
    setLocalFilters([])
    onFiltersChange([])
    onClear()
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <Filter className="h-4 w-4" />
          Advanced Filters
          {activeFiltersCount > 0 && (
            <Badge variant="secondary" className="ml-1">
              {activeFiltersCount}
            </Badge>
          )}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto" onOpenAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>Advanced Filters</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {localFilters.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No filters added yet. Click "Add Filter" to get started.
            </div>
          ) : (
            <div className="space-y-3">
              {localFilters.map((filter, index) => {
                const fieldConfig = getFieldConfig(filter.field)
                const operators = getOperatorsForField(filter.field)
                
                return (
                  <div key={filter.id} className="flex items-center gap-3 p-4 border rounded-lg">
                    {index > 0 && (
                      <Select
                        value={filter.connector || 'AND'}
                        onValueChange={(value: 'AND' | 'OR') => 
                          updateFilter(filter.id, { connector: value })
                        }
                      >
                        <SelectTrigger className="w-20">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="AND">AND</SelectItem>
                          <SelectItem value="OR">OR</SelectItem>
                        </SelectContent>
                      </Select>
                    )}

                    <Select
                      value={filter.field}
                      onValueChange={(value) => 
                        updateFilter(filter.id, { 
                          field: value, 
                          operator: getOperatorsForField(value)[0]?.value || 'contains',
                          value: '' 
                        })
                      }
                    >
                      <SelectTrigger className="w-48">
                        <SelectValue placeholder="Select field" />
                      </SelectTrigger>
                      <SelectContent>
                        {fields.map((field) => (
                          <SelectItem key={field.key} value={field.key}>
                            {field.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <Select
                      value={filter.operator}
                      onValueChange={(value) => updateFilter(filter.id, { operator: value })}
                    >
                      <SelectTrigger className="w-48">
                        <SelectValue placeholder="Select operator" />
                      </SelectTrigger>
                      <SelectContent>
                        {operators.map((op) => (
                          <SelectItem key={op.value} value={op.value}>
                            {op.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    {needsValueInput(filter.operator) && (
                      <div className="flex-1">
                        {fieldConfig?.type === 'select' ? (
                          <Select
                            value={filter.value}
                            onValueChange={(value) => updateFilter(filter.id, { value })}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select value..." />
                            </SelectTrigger>
                            <SelectContent>
                              {fieldConfig.options?.map((option) => (
                                <SelectItem key={option.value} value={option.value}>
                                  {option.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : fieldConfig?.type === 'boolean' ? (
                          <Select
                            value={filter.value}
                            onValueChange={(value) => updateFilter(filter.id, { value })}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select..." />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="true">Yes</SelectItem>
                              <SelectItem value="false">No</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : fieldConfig?.type === 'date' ? (
                          <Input
                            placeholder="Enter date (YYYY-MM-DD)..."
                            value={filter.value}
                            onChange={(e) => updateFilter(filter.id, { value: e.target.value })}
                            type="date"
                          />
                        ) : (
                          <Input
                            placeholder="Enter value..."
                            value={filter.value}
                            onChange={(e) => updateFilter(filter.id, { value: e.target.value })}
                            type={fieldConfig?.type === 'number' ? 'number' : 'text'}
                          />
                        )}
                      </div>
                    )}

                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeFilter(filter.id)}
                      className="text-destructive hover:text-destructive"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                )
              })}
            </div>
          )}

          <Button onClick={addFilter} variant="outline" className="w-full">
            <Plus className="h-4 w-4 mr-2" />
            Add Filter
          </Button>
        </div>

        <div className="flex justify-between pt-4 border-t">
          <Button variant="outline" onClick={handleClear}>
            Clear All
          </Button>
          <Button onClick={handleApply}>
            Apply Filters
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}